/* eslint-disable no-param-reassign,no-underscore-dangle */

/*
Copyright © 2021 EPAM Systems, Inc. All Rights Reserved.
All information contained herein is, and remains the property of EPAM Systems, Inc.
and/or its suppliers and is protected by international intellectual property law.
Dissemination of this information or reproduction of this material is strictly
forbidden, unless prior written permission is obtained from EPAM Systems, Inc
*/
/**
 * Sorts array by element property.
 *
 * @param {*} arr source
 * @param {*} property element property to sort by
 * @param {*} order
 */
export function sortBy(arr, property = 'name', order = 1) {
    let fn = property;
    if (typeof property === 'string') {
        if (property[0] === '-') {
            order = -1;
            property = property.substr(1);
        }
        fn = e => e[property];
    }

    function compare(a, b) {
        const aa = fn(a);
        const bb = fn(b);
        // eslint-disable-next-line no-nested-ternary
        return aa < bb ? -order : aa > bb ? order : 0;
    }

    return (arr || []).slice(0)
        .sort(compare);
}

export const dig = (o, steps) => (steps.reduce ? steps : steps.split('.')).reduce((r, e) => (r ? r[e] : undefined), o);
export const _def = (x, def) => (x == null ? def : x);
export const _digger = (steps, def) => o => _def(steps.reduce((r, e) => (r ? r[e] : r), o), def);

export const gettr = (path, def) => _digger(path.split('.'), def);
export const getFromLocalStorage = (key) => {
    // eslint-disable-next-line no-undef
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
};

export const getFromSessionStorage = (key) => {
    // eslint-disable-next-line no-undef
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
};

export const deepEqual = (x, y) => {
    const ok = Object.keys;
    const tx = typeof x;
    const ty = typeof y;
    return x && y && tx === 'object' && tx === ty && ok(x).length === ok(y).length
        ? ok(x)
            .every(key => deepEqual(x[key], y[key]))
        : x === y;
};

export const removeProperties = (object, keys) => Object.entries(object)
    .reduce(
        (prev, [ key, value ]) => ({
            ...prev,
            ...(!keys.includes(key) && { [key]: value }),
        }),
        {},
    );

export const comparePrimitives = (v, cv) => (typeof v === typeof cv ? v === cv : `${v}` === `${cv}`);

export const withDebounceFunction = (func, time) => {
    let timer;
    return (event) => {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(func, time, event);
    };
};

export const updatePaginationEntity = (entity, newItem) => {
    let {
        // eslint-disable-next-line prefer-const
        content,
        totalPages,
        last,
        totalElements,
        size,
    } = entity;
    const contentHasItem = content.find(({ id }) => id === newItem.id);
    if (contentHasItem) {
        return {
            ...entity,
            content: content.map(item => (item.id === newItem.id ? newItem : item)),
        };
    }

    if (content.length === 0) {
        totalPages += 1;
    }

    if (content.length === size) {
        content = [ newItem, ...content.splice(0, size - 1) ];
        if (totalElements % size === 0) {
            totalPages += 1;
        }
        last = false;
    } else {
        content = [ newItem, ...content ];
    }

    totalElements += 1;

    return {
        ...entity,
        content,
        totalPages,
        last,
        totalElements,
    };
};

export const makeDoctorsObj = (data, specializationId) => {
    let x = [...data];
    if (specializationId) {
        x = x.filter(doctor => doctor.specializationId.toString() === specializationId.toString());
    }
    return x.reduce((acc, item) => [ ...acc, {
        id: item.id,
        name: item.name,
    } ], []);
};

export const makeSpecialitiesObj = data => [...data].reduce((acc, item) => {
    if (acc.find(accItem => accItem.id.toString() === item.specializationId.toString())) {
        return acc;
    }
    return [ ...acc, {
        id: item.specializationId,
        name: item.specialityName,
    } ];
}, []);

export const getMetaDataByEnum = (o = {}) => Object.keys(o)
    .map(key => ({
        id: key,
        name: o[key],
    }));

export const arrShallowEqual = (x, y) => {
    if (!x || !y) {
        return false;
    }
    const lengthEqual = x.length === y.length;
    return (
        lengthEqual &&
        x.every(item => item.id === y.find(({ id }) => id === item.id).id)
    );
};

export const consoleLog = (text, ...args) => {
    // eslint-disable-next-line no-console
    console.log(`%c${text}`, 'color:fuchsia;border: 1px solid red;font-size: 16px;', ...args);
};

export const memoizeAsync = (fn) => {
    const cache = {};

    const memoizedFn = async (...args) => {
        const key = JSON.stringify(args);

        if (cache[key]) {
            return cache[key];
        }

        const promise = fn(...args);
        cache[key] = promise;

        try {
            return await promise;
        } catch (error) {
            delete cache[key]; // Remove the promise from the cache if it fails
            throw error;
        }
    };

    memoizedFn.clearCache = () => {
        Object.keys(cache).forEach(key => delete cache[key]);
    };

    return memoizedFn;
};
